import { useDragDropContext } from "../../utils/dragDropUtil";
import { createEffect, createSignal } from "solid-js";
import type { Id } from "@thisbeyond/solid-dnd";
import { createMousePosition } from "@solid-primitives/mouse";
import { useKeyDownEvent } from "@solid-primitives/keyboard";
import { KEY } from "../../utils/keyboardUtil";
import { isNil } from "lodash-es";
import DroppableList from "./DroppableList";
import type { MovableItem } from "./Moveable";
import type { Stringifiable } from "../../types";

const X_OFFSET_OVERLAY = 15;
const Y_OFFSET_OVERLAY = 35;
const MAX_SCROLL_SPEED = 200;
const SCROLL_ZONE_HEIGHT = 120;

type DragAndDropContainerProps<T extends Stringifiable> = {
  type: string;
  contentContainer: HTMLDivElement;
  items: MovableItem<T>[];
  onDrop: (draggableId: Id, droppableId: Id) => void;
};

export const DragAndDropContainer = <T extends Stringifiable>(props: DragAndDropContainerProps<T>) => {
  const [, { onDragStart, onDragEnd, dragEnd, onDragMove, addTransformer, removeTransformer, recomputeLayouts }] =
    useDragDropContext();
  const [ignoredItems, setIgnoredItems] = createSignal<Id[]>([]);
  const mousePosition = createMousePosition(window);
  const [isCancelled, setCancelled] = createSignal(false);
  const [intervalId, setIntervalId] = createSignal<NodeJS.Timeout>();
  const keyDownEvent = useKeyDownEvent();

  const onDragCancel = (e: KeyboardEvent) => {
    e.preventDefault();
    if (e.key === KEY.Escape) {
      setCancelled(true);
      dragEnd();
    }
  };

  /**
   * We need to track the current and next element to ignore their respective dropzones.
   * As we're rendering a dropzone above an element, we need to hide the dropzone both above and below the current element.
   * Otherwise, you'd be moving the element to the same position, which looks silly.
   */
  // oxlint-disable-next-line solid/reactivity
  onDragStart(({ draggable }) => {
    const currentItems = props.items.map((item) => ({
      id: item.id.toString(),
    }));
    const currentItemIndex = currentItems.findIndex((item) => item.id === draggable.id);
    if (currentItemIndex !== -1) {
      setIgnoredItems([
        draggable.id,
        ...(currentItemIndex < currentItems.length - 1 ? [currentItems[currentItemIndex + 1].id] : ["last"]),
      ]);
    }
    window.addEventListener("keydown", onDragCancel);

    const yDelta = mousePosition.y - draggable.layout.corners.topLeft.y;
    const xDelta = mousePosition.x - draggable.layout.corners.topLeft.x;

    addTransformer("draggables", draggable.id, {
      id: draggable.id,
      order: 0,
      callback: (transform) => ({
        x: transform.x + xDelta - X_OFFSET_OVERLAY,
        y: transform.y + yDelta - Y_OFFSET_OVERLAY,
      }),
    });
  });

  // oxlint-disable-next-line solid/reactivity
  onDragEnd(({ draggable, droppable }) => {
    clearInterval(intervalId());
    window.removeEventListener("keydown", onDragCancel);
    removeTransformer("draggables", draggable.id, draggable.id);

    if (isNil(droppable) || isCancelled()) {
      setCancelled(false);
      return;
    }

    props.onDrop(draggable.id, droppable.id);

    setIgnoredItems([]);
  });

  // oxlint-disable-next-line solid/reactivity
  onDragMove(() => {
    const id = intervalId();
    clearInterval(id);
    setIntervalId(undefined);

    const rect = props.contentContainer.getBoundingClientRect();
    const relativeX = mousePosition.x - rect!.left;
    const relativeY = mousePosition.y - rect!.top;
    const xInside = relativeX > 0 && relativeX < rect!.width;

    if (xInside && relativeY > 0 && relativeY < SCROLL_ZONE_HEIGHT) {
      setIntervalId(
        setInterval(() => {
          props.contentContainer.scrollBy(0, -MAX_SCROLL_SPEED / relativeY);
        }),
      );
    } else if (xInside && relativeY > rect!.height - SCROLL_ZONE_HEIGHT && relativeY < rect!.height) {
      setIntervalId(
        setInterval(() => {
          props.contentContainer.scrollBy(0, MAX_SCROLL_SPEED / (rect!.height - relativeY));
        }),
      );
    } else if (!isNil(id)) {
      // Re-computation is necessary since the position of the dropzones relative to the screen have changed
      recomputeLayouts();
    }
  });

  createEffect(() => {
    const activeElement = document.activeElement;
    if (activeElement === null) {
      return;
    }

    if (keyDownEvent()?.code === "ArrowDown" && activeElement.hasAttribute("data-container-trigger")) {
      const item = activeElement
        .closest(`[data-type="${props.type}"]`)
        ?.nextElementSibling?.querySelector("[data-container-trigger]");
      if (!isNil(item)) {
        (item as HTMLButtonElement).focus();
      }
    }

    if (keyDownEvent()?.code === "ArrowUp" && activeElement.hasAttribute("data-container-trigger")) {
      const item = activeElement
        .closest(`[data-type="${props.type}"]`)
        ?.previousElementSibling?.querySelector("[data-container-trigger]");
      if (!isNil(item)) {
        (item as HTMLButtonElement).focus();
      }
    }
  });

  return <DroppableList type={props.type} items={props.items} ignoredZones={ignoredItems()} />;
};
